import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import PaginationBar from '../components/PaginationBar';
import {
  Search, MapPin, Star, BookOpen, CheckCircle2,
  Sparkles, Filter
} from 'lucide-react';

const categoryLabels = {
  professional_training: 'Professional Training',
  tech_bootcamp: 'Tech Bootcamp',
  language: 'Language',
  creative: 'Creative',
  wellness: 'Wellness',
};

const categoryColors = {
  professional_training: 'bg-purple-100 text-purple-700',
  tech_bootcamp: 'bg-blue-100 text-blue-700',
  language: 'bg-emerald-100 text-emerald-700',
  creative: 'bg-pink-100 text-pink-700',
  wellness: 'bg-amber-100 text-amber-700',
};

function StarRating({ rating }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          className={`w-3.5 h-3.5 ${s <= Math.round(rating) ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}`}
        />
      ))}
      <span className="text-xs text-muted-foreground ml-1">{rating?.toFixed(1)}</span>
    </div>
  );
}

const PAGE_SIZE = 12;

export default function CourseProvidersPage() {
  const { api } = useAuth();
  const [providers, setProviders] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [location, setLocation] = useState('');

  // Any filter change starts again from the first page.
  useEffect(() => { setPage(1); }, [search, category, location]);

  useEffect(() => {
    fetchProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, category, location, page]);

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const params = { skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE };
      if (search) params.search = search;
      if (category) params.category = category;
      if (location) params.location = location;
      const res = await api.get('/course-providers', { params });
      setProviders(res.data?.providers || []);
      setTotal(res.data?.total || 0);
    } catch {
      setProviders([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProviders();
  };

  return (
    <div className="min-h-screen bg-[#F0F4F8]">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-700 to-fuchsia-700 text-white py-16 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Sparkles className="w-10 h-10 text-white/80" />
            <h1 className="text-4xl font-bold">Browse Course Providers & Trainers</h1>
          </div>
          <p className="text-white/80 text-lg mb-8">
            Discover short courses, workshops, seminars and professional training — not degree programmes
          </p>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="bg-white rounded-2xl p-3 flex flex-col sm:flex-row gap-3 max-w-3xl mx-auto shadow-lg">
            <div className="flex-1 flex items-center gap-2 px-2">
              <Search className="w-5 h-5 text-muted-foreground shrink-0" />
              <input
                type="text"
                placeholder="Search trainers, courses..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 text-gray-800 outline-none text-sm bg-transparent"
              />
            </div>

            <div className="flex gap-2">
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="text-sm text-gray-700 border border-gray-200 rounded-lg px-3 py-2 bg-white outline-none"
              >
                <option value="">All Categories</option>
                {Object.entries(categoryLabels).map(([k, label]) => (
                  <option key={k} value={k}>{label}</option>
                ))}
              </select>
              <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 bg-white">
                <MapPin className="w-4 h-4 text-gray-400 shrink-0" />
                <input
                  type="text"
                  placeholder="Location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="text-sm text-gray-700 outline-none w-24 bg-transparent"
                />
              </div>
              <Button type="submit" className="bg-purple-700 hover:bg-purple-800 text-white rounded-lg px-5">
                Search
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Results */}
      <div className="max-w-6xl mx-auto px-4 py-10">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {loading ? 'Loading...' : `${providers.length} result${providers.length !== 1 ? 's' : ''}`}
            </span>
          </div>
          <div className="flex gap-2">
            {category && (
              <Badge
                className="cursor-pointer bg-purple-100 text-purple-700 hover:bg-purple-200"
                onClick={() => setCategory('')}
              >
                {categoryLabels[category]} ×
              </Badge>
            )}
            {location && (
              <Badge
                className="cursor-pointer bg-gray-100 text-gray-700 hover:bg-gray-200"
                onClick={() => setLocation('')}
              >
                {location} ×
              </Badge>
            )}
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            Array(6).fill(0).map((_, i) => (
              <Card key={i}>
                <CardContent className="p-6 space-y-3">
                  <Skeleton className="h-14 w-14 rounded-xl" />
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-10 w-full" />
                </CardContent>
              </Card>
            ))
          ) : providers.length === 0 ? (
            <div className="col-span-3 text-center py-20">
              <Sparkles className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No results found</h3>
              <p className="text-muted-foreground">Try adjusting your search or filters.</p>
            </div>
          ) : (
            providers.map((p) => (
              <Card key={p.id} className="hover:shadow-lg transition-shadow group">
                <CardContent className="p-6">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-100 to-fuchsia-100 flex items-center justify-center mb-4 group-hover:from-purple-200 group-hover:to-fuchsia-200 transition-colors">
                    <Sparkles className="w-7 h-7 text-purple-700" />
                  </div>

                  <div className="flex items-start gap-2 mb-2">
                    <h3 className="font-bold text-foreground text-base leading-tight flex-1">{p.business_name}</h3>
                    {p.is_verified && (
                      <CheckCircle2 className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" title="Verified" />
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2 mb-3">
                    <Badge className={`text-xs ${categoryColors[p.category] || 'bg-gray-100 text-gray-700'}`}>
                      {categoryLabels[p.category] || p.category}
                    </Badge>
                  </div>

                  {p.location && (
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-2">
                      <MapPin className="w-3.5 h-3.5 shrink-0" />
                      {p.location}
                    </div>
                  )}

                  {p.average_rating > 0 && (
                    <div className="mb-2">
                      <StarRating rating={p.average_rating} />
                    </div>
                  )}

                  <p className="text-xs text-muted-foreground line-clamp-2 mb-4">{p.about}</p>

                  <div className="flex gap-4 text-xs text-muted-foreground mb-4">
                    <span className="flex items-center gap-1">
                      <BookOpen className="w-3.5 h-3.5" />
                      {(p.courses_count || 0).toLocaleString()} courses
                    </span>
                  </div>

                  <Link to={`/course-providers/${p.id}`}>
                    <Button className="w-full bg-purple-700 hover:bg-purple-800 text-white">
                      View Provider
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {!loading && providers.length > 0 && (
          <PaginationBar page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
        )}
      </div>
    </div>
  );
}
